/**
 * Aba Faturamento — relatório analítico com gráficos e tabelas.
 * Dimensões: bairro, plano, dia de vencimento e período (diário/mensal/anual).
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Avatar,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Chip,
  Alert,
  Tooltip,
  TableFooter,
  Divider,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  AttachMoney,
  Receipt,
  Payments,
  FilterList,
  ExpandMore,
  ExpandLess,
  Clear,
  Refresh,
  LocationOn,
  Speed,
  Event,
  CalendarMonth,
  Download,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { differenceInDays, parseISO } from 'date-fns';

import SysDateRangePicker from '../../components/SysDateRangePicker';
import PlanosSelect from '../../components/selects/PlanosSelect';
import BairroSelect from '../../components/selects/BairroSelect';
import { formatarData, formatarValor } from '../../utils/formatToView';
import { getRechartsStyles, getChartThemeColors, dashboardChartCardSx } from '../../styles/theme';
import {
  fmtInt,
  useRelatorioCores,
  parseYmdToLocalDate,
  dateToYmd,
  quickFilterOptions,
} from './relatorioUtils';
import type { FiltersFaturamento } from './relatorioTypes';
import {
  relatorioFaturamentoService,
  type DimensaoFaturamento,
  type GranularidadePeriodo,
} from '../../services/relatorioFaturamentoService';

const DIMENSOES: { key: DimensaoFaturamento; label: string; icon: React.ReactElement }[] = [
  { key: 'bairro', label: 'Por Bairro', icon: <LocationOn /> },
  { key: 'plano', label: 'Por Plano', icon: <Speed /> },
  { key: 'dia_vencimento', label: 'Por Dia Vencimento', icon: <Event /> },
  { key: 'periodo', label: 'Por Período', icon: <CalendarMonth /> },
];

const MAX_DIAS_DIARIO = 93;
const CHART_MAX_ITENS = 93;

const CHART_SERIES = {
  valorFaturado: { dataKey: 'valorFaturado', label: 'Valor faturado' },
  valorTotalEmitido: { dataKey: 'valorTotalEmitido', label: 'Total emitido' },
} as const;

type ChartSeriesKey = keyof typeof CHART_SERIES;

const FaturamentoChartCard: React.FC<{
  titulo: string;
  subtitulo?: string;
  onRefresh?: () => void;
  loading?: boolean;
  height?: number;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ titulo, subtitulo, onRefresh, loading, height = 300, headerExtra, children }) => {
  const theme = useTheme();
  return (
    <Card elevation={2} sx={{ ...dashboardChartCardSx(theme), mb: 1.5 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body1" fontWeight={700}>{titulo}</Typography>
            {subtitulo && (
              <Typography variant="caption" color="text.secondary">{subtitulo}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {headerExtra}
            {onRefresh && (
              <IconButton size="small" onClick={onRefresh} disabled={loading}>
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box sx={{ height, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export interface RelatorioFaturamentoTabProps {
  filters: FiltersFaturamento;
  onFilterChange: (updates: Partial<FiltersFaturamento>) => void;
  onClearFilters: () => void;
}

const RelatorioFaturamentoTab: React.FC<RelatorioFaturamentoTabProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const faturasColor = cores.bloqueado;
  const rechartsStyles = getRechartsStyles(theme);
  const chartColors = getChartThemeColors(theme);
  const corValorFaturado = theme.palette.success.main;
  const [searchParams] = useSearchParams();
  const escritorioId = searchParams.get('escritorio_id') || undefined;

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [agrupPage, setAgrupPage] = useState(0);
  const [agrupRows, setAgrupRows] = useState(10);
  const [regPage, setRegPage] = useState(0);
  const [regRows, setRegRows] = useState(25);
  const [selectedChave, setSelectedChave] = useState<string | null>(null);
  const [chartSeriesVisible, setChartSeriesVisible] = useState<Record<ChartSeriesKey, boolean>>({
    valorFaturado: true,
    valorTotalEmitido: true,
  });

  const toggleChartSeries = useCallback((key: ChartSeriesKey) => {
    setChartSeriesVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next.valorFaturado && !next.valorTotalEmitido) return prev;
      return next;
    });
  }, []);

  const corTotalEmitido = rechartsStyles.series.aReceber;

  const diasPeriodo = useMemo(() => {
    if (!filters.dataInicio || !filters.dataFim) return 0;
    try {
      return differenceInDays(parseISO(filters.dataFim), parseISO(filters.dataInicio)) + 1;
    } catch {
      return 0;
    }
  }, [filters.dataInicio, filters.dataFim]);

  const periodoDiarioInvalido =
    filters.dimensao === 'periodo' &&
    filters.granularidade === 'diario' &&
    diasPeriodo > MAX_DIAS_DIARIO;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [
      'relatorio-faturamento',
      escritorioId,
      filters.dataInicio,
      filters.dataFim,
      filters.dimensao,
      filters.granularidade,
      filters.bairro,
      filters.planoId,
      filters.diaVencimento,
    ],
    queryFn: () =>
      relatorioFaturamentoService.getRelatorio({
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim,
        dimensao: filters.dimensao,
        granularidade: filters.granularidade,
        bairro: filters.bairro,
        planoId: filters.planoId,
        diaVencimento: filters.diaVencimento,
        escritorioId,
      }),
    enabled: !!filters.dataInicio && !!filters.dataFim && !periodoDiarioInvalido,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const summary = data?.summary;
  const agrupamentos = data?.agrupamentos ?? [];
  const registros = data?.registros ?? [];

  const registrosFiltrados = useMemo(() => {
    if (!selectedChave) return registros;
    return registros.filter((r) => r.periodo_chave === selectedChave || String(r.dia_vencimento) === selectedChave);
  }, [registros, selectedChave]);

  const chartData = useMemo(() => {
    let items = [...agrupamentos];
    if (filters.dimensao !== 'periodo') {
      items = items
        .sort((a, b) => Math.max(b.valor_faturado, b.valor_total_emitido) - Math.max(a.valor_faturado, a.valor_total_emitido))
        .slice(0, CHART_MAX_ITENS);
    } else if (items.length > CHART_MAX_ITENS) {
      items = items.slice(-CHART_MAX_ITENS);
    }
    return items.map((a) => ({
      mes: a.label,
      fullLabel:
        filters.dimensao === 'periodo' && filters.granularidade === 'diario' && /^\d{4}-\d{2}-\d{2}/.test(a.chave)
          ? (() => {
              const [y, m, d] = a.chave.split('-');
              return `${d}/${m}/${y}`;
            })()
          : filters.dimensao === 'periodo' && filters.granularidade === 'mensal' && /^\d{4}-\d{2}$/.test(a.chave)
            ? (() => {
                const [y, m] = a.chave.split('-');
                return `${m}/${y}`;
              })()
            : a.label,
      valorFaturado: a.valor_faturado,
      valorTotalEmitido: a.valor_total_emitido,
    }));
  }, [agrupamentos, filters.dimensao, filters.granularidade]);

  const chartSubtitulo = useMemo(() => {
    const dim = DIMENSOES.find((d) => d.key === filters.dimensao)?.label ?? '';
    const totalPago = formatarValor(summary?.total_faturado ?? 0);
    const totalEmitido = formatarValor(summary?.total_emitido ?? 0);
    const exibidos = chartData.length;
    const totalGrupos = agrupamentos.length;
    const limite =
      totalGrupos > CHART_MAX_ITENS
        ? ` · exibindo ${exibidos} de ${totalGrupos} grupos`
        : '';
    return `${dim} · pago ${totalPago} · emitido ${totalEmitido} (exc. canceladas)${limite}`;
  }, [filters.dimensao, summary?.total_faturado, summary?.total_emitido, chartData.length, agrupamentos.length]);

  const handleDimensaoChange = (dim: DimensaoFaturamento) => {
    setSelectedChave(null);
    setAgrupPage(0);
    setRegPage(0);
    onFilterChange({ dimensao: dim });
  };

  const handleQuickFilter = (value: string) => {
    const opt = quickFilterOptions.find((o) => o.value === value);
    if (!opt) return;
    const { startDate, endDate } = opt.getRange();
    onFilterChange({
      quickFilter: value,
      dataInicio: dateToYmd(startDate),
      dataFim: dateToYmd(endDate),
    });
  };

  const exportCSV = useCallback(() => {
    if (!registros.length) return;
    let csv = 'Fatura;Cliente;Contrato;Descrição;Valor Pago;Pagamento;Vencimento;Bairro;Plano;Dia Vcto;Forma Pgto\n';
    registros.forEach((r) => {
      csv += `${r.fatura_id};"${r.cliente_nome}";"${r.contrato_titulo}";"${r.descricao}";${r.valor_pago};"${formatarData(r.data_pagamento)}";"${formatarData(r.data_vencimento)}";"${r.bairro_inst}";"${r.plano_nome}";${r.dia_vencimento ?? ''};"${r.forma_pagamento}"\n`;
    });
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_faturamento_${filters.dimensao}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [registros, filters.dimensao]);

  const hasActiveFilters =
    filters.bairro || filters.planoId || filters.diaVencimento;

  const dimensaoIdx = DIMENSOES.findIndex((d) => d.key === filters.dimensao);

  const tableHeadRowSx = {
    '& .MuiTableCell-head': {
      bgcolor: (t: typeof theme) => `color-mix(in srgb, ${faturasColor} 12%, ${t.palette.background.paper})`,
      fontWeight: 700,
      fontSize: '0.73rem',
      py: 0.8,
      borderBottom: `2px solid ${alpha(faturasColor, 0.2)}`,
    },
  };

  const tableBodyRowSx = {
    '&:hover': { bgcolor: alpha(faturasColor, 0.03) },
  };

  const tableCellSx = { py: 0.5, fontSize: '0.78rem' };

  const tableFooterRowSx = {
    '& .MuiTableCell-root': {
      bgcolor: (t: typeof theme) => `color-mix(in srgb, ${faturasColor} 15%, ${t.palette.background.paper})`,
      fontWeight: 800,
      fontSize: '0.78rem',
      borderTop: `2px solid ${alpha(faturasColor, 0.3)}`,
      py: 1.2,
      color: 'text.primary',
    },
  };

  const paginationBoxSx = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: 0.8,
    px: 2,
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  };

  const tablePaginationSx = {
    width: '100%',
    '.MuiTablePagination-toolbar': {
      minHeight: 32,
      flexWrap: 'nowrap',
      justifyContent: 'center',
      gap: 1,
      p: 0,
      '& .MuiTablePagination-spacer': { display: 'none' },
    },
    '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
    '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
    '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
    '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
  };

  const showDetalhesCol = filters.dimensao === 'periodo' && filters.granularidade === 'diario';
  const agrupColCount = showDetalhesCol ? 6 : 5;

  const agrupPageData = agrupamentos.slice(agrupPage * agrupRows, agrupPage * agrupRows + agrupRows);
  const agrupTotalPagina = agrupPageData.reduce((s, r) => s + r.valor_faturado, 0);
  const agrupTotalEmitidoPagina = agrupPageData.reduce((s, r) => s + r.valor_total_emitido, 0);
  const agrupFaturasPagina = agrupPageData.reduce((s, r) => s + r.quantidade_faturas, 0);
  const agrupPagamentosPagina = agrupPageData.reduce((s, r) => s + r.quantidade_pagamentos, 0);

  const regPageData = registrosFiltrados.slice(regPage * regRows, regPage * regRows + regRows);
  const regTotalPagina = regPageData.reduce((s, r) => s + r.valor_pago, 0);

  return (
    <Box>
      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {[
          { label: 'Total Faturado', value: formatarValor(summary?.total_faturado ?? 0), icon: <AttachMoney />, color: cores.ativo },
          { label: 'Total Emitido', value: formatarValor(summary?.total_emitido ?? 0), icon: <Receipt />, color: cores.primario },
          { label: 'Faturas Pagas', value: fmtInt(summary?.total_faturas ?? 0), icon: <Receipt />, color: '#0288d1' },
          { label: 'Pagamentos', value: fmtInt(summary?.total_pagamentos ?? 0), icon: <Payments />, color: cores.bloqueado },
        ].map((kpi) => (
          <Grid item xs={6} sm={3} key={kpi.label}>
            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isLoading ? (
                <Skeleton variant="rounded" width="100%" height={56} />
              ) : (
                <>
                  <Avatar sx={{ bgcolor: alpha(kpi.color, 0.12), color: kpi.color, width: 44, height: 44 }}>
                    {kpi.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      {kpi.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                      {kpi.value}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Dimensão */}
      <Paper elevation={1} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={dimensaoIdx >= 0 ? dimensaoIdx : 0}
          onChange={(_e, idx) => handleDimensaoChange(DIMENSOES[idx].key)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', minHeight: 40 },
          }}
        >
          {DIMENSOES.map((d) => (
            <Tab key={d.key} icon={React.cloneElement(d.icon, { sx: { fontSize: 16 } })} iconPosition="start" label={d.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Filtros */}
      <Paper elevation={1} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', bgcolor: alpha(cores.primario, 0.04),
          }}
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ fontSize: 18, color: cores.primario }} />
            <Typography variant="subtitle2" fontWeight={700}>Filtros</Typography>
            {hasActiveFilters && <Chip label="Ativos" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Atualizar">
              <span>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); refetch(); }} disabled={isFetching}>
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {filtersOpen ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </Box>
        <Collapse in={filtersOpen}>
          <Box sx={{ p: 2, pt: 0 }}>
            <Grid container spacing={1.5} sx={{mt: 0.5}} alignItems="center">
              <Grid item xs={12} md={2.5}>
                <SysDateRangePicker
                  label="Período de pagamento"
                  value={{
                    startDate: parseYmdToLocalDate(filters.dataInicio),
                    endDate: parseYmdToLocalDate(filters.dataFim),
                  }}
                  onChange={(range) =>
                    onFilterChange({
                      dataInicio: dateToYmd(range.startDate),
                      dataFim: dateToYmd(range.endDate),
                      quickFilter: '',
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Filtro rápido"
                  value={filters.quickFilter || ''}
                  onChange={(e) => handleQuickFilter(e.target.value)}
                >
                  {quickFilterOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {filters.dimensao === 'periodo' && (
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Agrupamento do período"
                    value={filters.granularidade}
                    onChange={(e) =>
                      onFilterChange({ granularidade: e.target.value as GranularidadePeriodo })
                    }
                  >
                    <MenuItem value="diario">Diário (máx. 3 Meses)</MenuItem>
                    <MenuItem value="mensal">Mensal</MenuItem>
                    <MenuItem value="anual">Anual</MenuItem>
                  </TextField>
                </Grid>
              )}
              </Grid>
              <Grid container spacing={1.5} sx={{mt: 0.5}} alignItems="center">
              <Grid item xs={12} md={4}>
                <BairroSelect
                  value={filters.bairro || null}
                  onChange={(v) => onFilterChange({ bairro: v || '' })}
                  label="Bairro do contrato"
                  size="small"
                  allowClear
                  escritorio_id={escritorioId ? Number(escritorioId) : undefined}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PlanosSelect
                  value={filters.planoId ? Number(filters.planoId) : null}
                  onChange={(v) => onFilterChange({ planoId: v ? String(v) : '' })}
                  label="Plano (serviço vinculado)"
                  size="small"
                  fullWidth
                  allowClear
                  onlyActive={false}
                  escritorio_id={escritorioId ? Number(escritorioId) : undefined}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Dia de vencimento do contrato"
                  value={filters.diaVencimento}
                  onChange={(e) => onFilterChange({ diaVencimento: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <MenuItem key={d} value={String(d)}>Dia {d}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" startIcon={<Clear />} onClick={onClearFilters}>
                    Limpar filtros
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Download />}
                    onClick={exportCSV}
                    disabled={!registros.length}
                  >
                    Exportar CSV
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {periodoDiarioInvalido && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          O agrupamento diário aceita no máximo {MAX_DIAS_DIARIO} dias. Período selecionado: {diasPeriodo} dias.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {(error as Error).message || 'Erro ao carregar relatório de faturamento.'}
        </Alert>
      )}

      {/* Gráfico */}
      <FaturamentoChartCard
        titulo={`Faturamento — ${DIMENSOES.find((d) => d.key === filters.dimensao)?.label}`}
        subtitulo={chartSubtitulo}
        onRefresh={() => refetch()}
        loading={isLoading || isFetching}
        height={300}
        headerExtra={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Tooltip title="Clique para exibir ou ocultar no gráfico">
              <Chip
                size="small"
                label={CHART_SERIES.valorFaturado.label}
                onClick={() => toggleChartSeries('valorFaturado')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 26,
                  border: `1.5px solid ${corValorFaturado}`,
                  bgcolor: chartSeriesVisible.valorFaturado ? alpha(corValorFaturado, 0.15) : 'transparent',
                  color: chartSeriesVisible.valorFaturado ? corValorFaturado : theme.palette.text.disabled,
                  opacity: chartSeriesVisible.valorFaturado ? 1 : 0.55,
                  textDecoration: chartSeriesVisible.valorFaturado ? 'none' : 'line-through',
                  '&:hover': { bgcolor: alpha(corValorFaturado, chartSeriesVisible.valorFaturado ? 0.25 : 0.08) },
                }}
              />
            </Tooltip>
            <Tooltip title="Clique para exibir ou ocultar no gráfico">
              <Chip
                size="small"
                label={CHART_SERIES.valorTotalEmitido.label}
                onClick={() => toggleChartSeries('valorTotalEmitido')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 26,
                  border: `1.5px solid ${corTotalEmitido}`,
                  bgcolor: chartSeriesVisible.valorTotalEmitido ? alpha(corTotalEmitido, 0.15) : 'transparent',
                  color: chartSeriesVisible.valorTotalEmitido ? corTotalEmitido : theme.palette.text.disabled,
                  opacity: chartSeriesVisible.valorTotalEmitido ? 1 : 0.55,
                  textDecoration: chartSeriesVisible.valorTotalEmitido ? 'none' : 'line-through',
                  '&:hover': { bgcolor: alpha(corTotalEmitido, chartSeriesVisible.valorTotalEmitido ? 0.25 : 0.08) },
                }}
              />
            </Tooltip>
          </Box>
        )}
      >
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary">Nenhum dado para o período selecionado.</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="corFaturado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={corValorFaturado} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={corValorFaturado} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="corEmitido" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={rechartsStyles.series.aReceber} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={rechartsStyles.series.aReceber} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="mes" tick={{ fill: chartColors.axis, fontSize: 11 }} />
              <YAxis
                tick={{ fill: chartColors.axis, fontSize: 11 }}
                tickFormatter={(v) => formatarValor(v)}
              />
              <RechartsTooltip
                contentStyle={rechartsStyles.tooltip.contentStyle}
                labelFormatter={(_label, payload) =>
                  (payload?.[0]?.payload as { fullLabel?: string })?.fullLabel ?? ''
                }
                formatter={(value: number, name: string) => [formatarValor(value), name]}
              />
              <Legend
                wrapperStyle={{ color: rechartsStyles.legend.color, fontSize: 11, cursor: 'pointer' }}
                onClick={(entry) => {
                  const key = entry?.dataKey as ChartSeriesKey | undefined;
                  if (key && key in CHART_SERIES) toggleChartSeries(key);
                }}
                formatter={(value, entry) => {
                  const key = entry.dataKey as ChartSeriesKey;
                  const active = chartSeriesVisible[key];
                  return (
                    <span style={{ color: active ? entry.color : theme.palette.text.disabled, opacity: active ? 1 : 0.45 }}>
                      {value}
                    </span>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="valorTotalEmitido"
                name="Total emitido"
                stroke={corTotalEmitido}
                fill="url(#corEmitido)"
                strokeWidth={2}
                hide={!chartSeriesVisible.valorTotalEmitido}
              />
              <Area
                type="monotone"
                dataKey="valorFaturado"
                name="Valor faturado"
                stroke={corValorFaturado}
                fill="url(#corFaturado)"
                strokeWidth={2}
                hide={!chartSeriesVisible.valorFaturado}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </FaturamentoChartCard>

      {/* Tabela agrupamentos */}
      <Paper elevation={2} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', m: 0 }}>
            Resumo por agrupamento
          </Typography>
        </Box>
        <Divider sx={{ mt: 1, mb: 0 }} />
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell>Grupo</TableCell>
                <TableCell align="right">Valor Faturado</TableCell>
                <TableCell align="right">Total Emitido</TableCell>
                <TableCell align="right">Faturas</TableCell>
                <TableCell align="right">Pagamentos</TableCell>
                {showDetalhesCol && <TableCell align="center">Detalhes</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={agrupColCount} align="center"><Skeleton /></TableCell>
                    </TableRow>
                  ))
                : agrupPageData.map((row) => (
                    <TableRow
                      key={row.chave}
                      hover
                      selected={selectedChave === row.chave}
                      sx={{
                        ...tableBodyRowSx,
                        cursor: showDetalhesCol ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (showDetalhesCol) {
                          setSelectedChave((prev) => (prev === row.chave ? null : row.chave));
                          setRegPage(0);
                        }
                      }}
                    >
                      <TableCell sx={{ ...tableCellSx, fontWeight: 600 }}>{row.label}</TableCell>
                      <TableCell align="right" sx={{ ...tableCellSx, fontWeight: 600 }}>
                        {formatarValor(row.valor_faturado)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...tableCellSx, fontWeight: 600 }}>
                        {formatarValor(row.valor_total_emitido)}
                      </TableCell>
                      <TableCell align="right" sx={tableCellSx}>{fmtInt(row.quantidade_faturas)}</TableCell>
                      <TableCell align="right" sx={tableCellSx}>{fmtInt(row.quantidade_pagamentos)}</TableCell>
                      {showDetalhesCol && (
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip
                            size="small"
                            label={selectedChave === row.chave ? 'Ocultar' : 'Ver faturas'}
                            color={selectedChave === row.chave ? 'primary' : 'default'}
                            sx={{ fontSize: '0.68rem', height: 22 }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              {!isLoading && agrupamentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={agrupColCount} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">Nenhum agrupamento encontrado.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!isLoading && agrupPageData.length > 0 && (
              <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <TableRow sx={tableFooterRowSx}>
                  <TableCell align="right" sx={{ letterSpacing: '0.05em' }}>TOTAL DA PÁGINA</TableCell>
                  <TableCell align="right">{formatarValor(agrupTotalPagina)}</TableCell>
                  <TableCell align="right">{formatarValor(agrupTotalEmitidoPagina)}</TableCell>
                  <TableCell align="right">{fmtInt(agrupFaturasPagina)}</TableCell>
                  <TableCell align="right">{fmtInt(agrupPagamentosPagina)}</TableCell>
                  {showDetalhesCol && <TableCell />}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        {agrupamentos.length > 0 && (
          <Box sx={paginationBoxSx}>
            <TablePagination
              component="div"
              count={agrupamentos.length}
              page={agrupPage}
              onPageChange={(_e, p) => setAgrupPage(p)}
              rowsPerPage={agrupRows}
              onRowsPerPageChange={(e) => { setAgrupRows(parseInt(e.target.value, 10)); setAgrupPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={tablePaginationSx}
            />
          </Box>
        )}
      </Paper>

      {/* Tabela registros (faturas pagas) */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 1.5,
            pt: 1.5,
            pb: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', m: 0 }}>
              Registros de faturas pagas
            </Typography>
            {selectedChave && (
              <Chip
                size="small"
                label={`Filtrado: ${agrupamentos.find((a) => a.chave === selectedChave)?.label ?? selectedChave}`}
                onDelete={() => setSelectedChave(null)}
                sx={{ height: 22, fontSize: '0.68rem' }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            {fmtInt(registrosFiltrados.length)} registro(s)
          </Typography>
        </Box>
        <Divider sx={{ mt: 1, mb: 0 }} />
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, maxHeight: 480 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell>Fatura</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Contrato</TableCell>
                <TableCell align="right">Valor Pago</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Bairro</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell align="center">Dia Vcto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={9} align="center"><Skeleton /></TableCell>
                    </TableRow>
                  ))
                : regPageData.map((r) => (
                    <TableRow key={`${r.fatura_id}-${r.data_pagamento}`} hover sx={tableBodyRowSx}>
                      <TableCell sx={tableCellSx}>
                        <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.78rem' }}>
                          #{r.fatura_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="primary" fontWeight="medium" sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {r.cliente_nome || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="secondary" sx={{ fontSize: '0.78rem' }}>
                          {r.contrato_titulo || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ ...tableCellSx, fontWeight: 600 }}>
                        {formatarValor(r.valor_pago)}
                      </TableCell>
                      <TableCell sx={tableCellSx}>{formatarData(r.data_pagamento)}</TableCell>
                      <TableCell sx={tableCellSx}>{formatarData(r.data_vencimento)}</TableCell>
                      <TableCell sx={tableCellSx}>{r.bairro_inst || '—'}</TableCell>
                      <TableCell sx={{ ...tableCellSx, whiteSpace: 'nowrap' }}>{r.plano_nome || '—'}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>{r.dia_vencimento ?? '—'}</TableCell>
                    </TableRow>
                  ))}
              {!isLoading && registrosFiltrados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">Nenhuma fatura paga no período.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!isLoading && regPageData.length > 0 && (
              <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <TableRow sx={tableFooterRowSx}>
                  <TableCell colSpan={3} align="right" sx={{ letterSpacing: '0.05em' }}>TOTAL DA PÁGINA</TableCell>
                  <TableCell align="right">{formatarValor(regTotalPagina)}</TableCell>
                  <TableCell colSpan={5} />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        {registrosFiltrados.length > 0 && (
          <Box sx={paginationBoxSx}>
            <TablePagination
              component="div"
              count={registrosFiltrados.length}
              page={regPage}
              onPageChange={(_e, p) => setRegPage(p)}
              rowsPerPage={regRows}
              onRowsPerPageChange={(e) => { setRegRows(parseInt(e.target.value, 10)); setRegPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={tablePaginationSx}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RelatorioFaturamentoTab;
